import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { EventEmitter } from '@flipster/utils/event-emitter';

export const isPipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

interface DocumentPictureInPictureOptions {
  width?: number;
  height?: number;
}

function checkPipSupported(win?: Window): win is Window & {
  documentPictureInPicture: { requestWindow: (options?: DocumentPictureInPictureOptions) => Promise<Window> };
} {
  return (
    typeof win !== 'undefined' &&
    'documentPictureInPicture' in win &&
    typeof win.documentPictureInPicture?.requestWindow === 'function'
  );
}

export function showPipNotSupportedAlert() {
  dialog.open({
    title: {
      label: 'Update browser to use PiP mode',
    },
    content:
      "Your current browser version doesn't support Web PiP. Please update it or switch to a supported browser to continue.",
    buttons: {
      primary: <Dialog.Button hierarchy='primary' label='Confirm' />,
    },
  });
}

async function openPipWindow(options: DocumentPictureInPictureOptions) {
  if (!checkPipSupported(window)) {
    showPipNotSupportedAlert();
    return null;
  }
  try {
    const pipWindow = await window.documentPictureInPicture.requestWindow(options);

    const pendingLoads: Promise<unknown>[] = [];

    // PiP 윈도우에는 스타일시트(<style> / <link rel="stylesheet">)만 복제한다.
    // <meta> 나 preload·modulepreload·icon·manifest 등 스타일과 무관한 <link> 를
    // PiP 문서 head 에 주입하면, 최신 크롬에서 PiP 렌더러가 허용되지 않은 IPC
    // (리소스 로드/파비콘 갱신 등)를 전송하면서 RESULT_CODE_KILLED_BAD_MESSAGE 로
    // 렌더러가 강제 종료된다. 이 종료는 JS 예외가 아니라 프로세스 크래시이므로
    // 위의 try/catch 로도 잡을 수 없다.
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((el) => {
      if (!pipWindow) return;

      if (el.tagName.toLowerCase() === 'link') {
        const link = el as HTMLLinkElement;
        if (!link.href) return;

        const newLink = pipWindow.document.createElement('link');
        newLink.rel = 'stylesheet';
        newLink.href = link.href;
        pipWindow.document.head.appendChild(newLink);

        pendingLoads.push(
          new Promise((resolve, reject) => {
            newLink.onload = resolve;
            newLink.onerror = reject;
          }),
        );
      } else {
        pipWindow.document.head.appendChild(el.cloneNode(true));
      }
    });

    await Promise.allSettled(pendingLoads);
    return pipWindow;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return null;
    }
    // oxlint-disable-next-line no-console
    console.error(error);
    return null;
  }
}

function createPipPortal() {
  const emitter = new EventEmitter<{ open: Window; close: null }>();

  let pipWindow: Window | null = null;

  async function open(options: DocumentPictureInPictureOptions = {}) {
    if (pipWindow) return;
    pipWindow = await openPipWindow(options);
    if (!pipWindow) return;
    pipWindow.addEventListener('pagehide', close, { once: true });
    emitter.emit('open', pipWindow);
  }

  function close() {
    if (!pipWindow) return;
    pipWindow?.close();
    pipWindow?.removeEventListener('pagehide', close);
    pipWindow = null;
    emitter.emit('close', null);
  }

  const PipPortal = ({
    autoPipOnVisibilityChange,
    children,
    onPipOpen,
    onPipClose,
  }: {
    autoPipOnVisibilityChange?: boolean;
    children: ReactNode;
    onPipOpen?: () => void;
    onPipClose?: () => void;
  }) => {
    const [pip, setPip] = useState<Window | null>(null);

    useEffect(() => {
      const handleOpen = (params: Window | null) => {
        setPip(params);
        onPipOpen?.();
      };

      const handleClose = (params: Window | null) => {
        setPip(params);
        onPipClose?.();
      };

      emitter.on('open', handleOpen);
      emitter.on('close', handleClose);
      return () => {
        emitter.off('open', handleOpen);
        emitter.off('close', handleClose);
      };
    }, [onPipOpen, onPipClose]);

    useEffect(() => {
      function focusListener() {
        requestAnimationFrame(() => {
          if (document.hasFocus() && document.visibilityState === 'visible') {
            close();
          }
        });
      }
      function visibilityListener() {
        if (autoPipOnVisibilityChange && document.visibilityState === 'hidden') open();
        if (document.visibilityState === 'visible') close();
      }
      window.addEventListener('focus', focusListener);
      window.addEventListener('visibilitychange', visibilityListener);
      return () => {
        window.removeEventListener('focus', focusListener);
        window.removeEventListener('visibilitychange', visibilityListener);
      };
    }, [autoPipOnVisibilityChange, pip]);

    if (!pip) return null;
    return createPortal(children, pip.document.body);
  };

  return { PipPortal, pip: { open, close } };
}

export const { PipPortal, pip } = createPipPortal();
